import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';
import { usePageTitle } from '../hooks/usePageTitle';

const Home3DShowcase = lazy(() => import('../components/Home3DShowcase'));

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  usePageTitle(); // Usa título padrão
  const [weeklyHighlights, setWeeklyHighlights] = useState(null);
  const [highlightProducts, setHighlightProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showcaseProduct, setShowcaseProduct] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchWeeklyHighlights();
    fetchSettings();
  }, []);

  useEffect(() => {
    // Busca o produto do showcase 3D — prioriza rodízio semanal (hero_3d_current_product_id),
    // e cai para o fallback manual (hero_3d_product_id) se rotação estiver vazia.
    const effectiveId = settings?.hero_3d_current_product_id || settings?.hero_3d_product_id;
    if (effectiveId) {
      (async () => {
        try {
          const res = await axios.get(`${API_URL}/products/${effectiveId}`);
          if (res.data?.model_3d_url) setShowcaseProduct(res.data);
          else setShowcaseProduct(null);
        } catch (_) {
          setShowcaseProduct(null);
        }
      })();
    } else {
      setShowcaseProduct(null);
    }
  }, [settings?.hero_3d_current_product_id, settings?.hero_3d_product_id]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/settings`);
      setSettings(res.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchWeeklyHighlights = async () => {
    try {
      const highlightsRes = await axios.get(`${API_URL}/weekly-highlights`);
      setWeeklyHighlights(highlightsRes.data);
      const ids = highlightsRes.data?.product_ids || [];
      if (ids.length === 0) return;

      // Busca só os produtos em destaque (limite ao que precisa) — evita baixar catálogo inteiro
      const productsRes = await axios.get(`${API_URL}/products?featured=true`);
      const productsMap = new Map(productsRes.data.map((p) => [p.id, p]));
      // fallback: se algum id destacado não estiver marcado como featured, complementa
      const missing = ids.filter((id) => !productsMap.has(id));
      if (missing.length > 0) {
        try {
          const batchRes = await axios.post(`${API_URL}/products/batch`, { product_ids: missing });
          (batchRes.data || []).forEach((p) => productsMap.set(p.id, p));
        } catch (_) {
      // Silêncio proposital: se o batch complementar falhar, mostramos só o que já obtivemos via ?featured=true.
    }
      }
      setHighlightProducts(ids.map((id) => productsMap.get(id)).filter(Boolean));
    } catch (error) {
      console.error('Failed to fetch highlights:', error);
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center">
        <div className="absolute inset-0">
          <img
            src={settings?.hero_image || "https://images.unsplash.com/photo-1760072513367-55182245e76c?q=85&w=1920&auto=format&fit=crop"}
            alt="Luxury Furniture"
            fetchPriority="high"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-primary opacity-30"></div>
        </div>

        <div className="relative z-10 px-6 md:px-12 lg:px-24 max-w-7xl">
          <h1 data-testid="hero-title" className="font-heading text-5xl md:text-7xl text-white font-light tracking-tight leading-tight mb-6">
            Elegância que<br />Transforma Espaços
          </h1>
          <p className="text-lg font-light leading-relaxed text-white/90 mb-12 max-w-xl">
            Móveis de alto padrão para residências e ambientes corporativos sofisticados.
          </p>
          <Link
            to="/catalogo"
            data-testid="explore-catalog-button"
            className="inline-flex items-center gap-3 bg-white text-primary px-8 py-4 uppercase tracking-widest text-xs hover:bg-primary hover:text-white border border-white transition-all duration-500"
          >
            Explorar Catálogo
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* Weekly Highlights Section */}
      {highlightProducts.length > 0 && (
        <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-secondary">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight mb-4">
              {weeklyHighlights?.title || 'Destaques da Semana'}
            </h2>
            <p className="text-base font-light leading-relaxed text-muted-foreground max-w-2xl mx-auto">
              {weeklyHighlights?.subtitle || 'Seleção especial para você'}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {highlightProducts.slice(0, 4).map((product) => (
              <Link
                key={product.id}
                to={`/produto/${product.id}`}
                className="group"
              >
                <div className="aspect-[4/3] overflow-hidden bg-neutral-50 mb-4">
                  <img
                    src={product.images?.[0] || '/placeholder.svg'}
                    alt={product.name}
                    loading="lazy"
                    className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <h3 className="text-sm font-medium mb-1 group-hover:text-primary transition-colors">
                  {product.name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  R$ {product.price?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/catalogo"
              className="inline-flex items-center gap-3 border-b border-primary text-primary uppercase tracking-widest text-xs hover:border-primary/70 hover:text-primary/70 transition-all duration-300 pb-1"
            >
              Ver Todo o Catálogo
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </section>
      )}

      {/* Categories Bento Grid */}
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <h2 data-testid="categories-title" className="font-heading text-4xl md:text-5xl font-light tracking-tight mb-4">
            Nossas Coleções
          </h2>
          <p className="text-base font-light leading-relaxed text-muted-foreground max-w-2xl mx-auto">
            Descubra móveis exclusivos para cada ambiente
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-border">
          <Link
            to="/catalogo?category=residencial"
            data-testid="residential-category"
            className="group relative aspect-square overflow-hidden border-b md:border-r border-border"
          >
            <img
              src={settings?.home_residential_image || "https://customer-assets-39nsmqrw.emergentagent.net/job_9be541c9-256c-474c-9103-b74c8cc43bdb/artifacts/eldt7h6m_Projetos%20com%20identidade%20exigem%20planejamento.Adequar%20materiais%2C%20propor%C3%A7%C3%B5es%20e%20solu%C3%A7%C3%B5es%20ao%20estilo%20d.jpg"}
              alt="Residencial"
              className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-primary/20 group-hover:bg-primary/40 transition-colors duration-500"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <h3 className="font-heading text-4xl text-white">Residencial</h3>
            </div>
          </Link>

          <Link
            to="/catalogo?category=corporativo"
            data-testid="corporate-category"
            className="group relative aspect-square overflow-hidden border-b border-border"
          >
            <img
              src={settings?.home_corporate_image || "https://images.unsplash.com/photo-1542089078-9086d071d8da?q=85&w=800&auto=format&fit=crop"}
              alt="Corporativo"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-primary/20 group-hover:bg-primary/40 transition-colors duration-500"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <h3 className="font-heading text-4xl text-white">Corporativo</h3>
            </div>
          </Link>
        </div>
      </section>

      {/* 3D Showcase — vitrine da peça em destaque */}
      {showcaseProduct && (
        <Suspense fallback={<div className="h-[400px] bg-neutral-950" />}>
          <Home3DShowcase
            product={showcaseProduct}
            title={settings?.hero_3d_title}
            subtitle={settings?.hero_3d_subtitle}
          />
        </Suspense>
      )}

      {/* Featured Section */}
      <section className="py-20 md:py-32 bg-secondary">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight mb-6">
                Qualidade Artesanal
              </h2>
              <p className="text-base font-light leading-relaxed text-muted-foreground mb-8">
                Cada peça é cuidadosamente projetada e confeccionada por mestres artesãos,
                utilizando apenas os melhores materiais. Nossa dedicação à excelência garante
                móveis que atravessam gerações.
              </p>
              <Link
                to="/sobre"
                data-testid="learn-more-button"
                className="inline-flex items-center gap-3 border-b border-primary text-primary uppercase tracking-widest text-xs hover:border-primary/70 hover:text-primary/70 transition-all duration-300 pb-1"
              >
                Saiba Mais
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
            <div>
              <img
                src={settings?.about_home_image || "https://customer-assets-39nsmqrw.emergentagent.net/job_9be541c9-256c-474c-9103-b74c8cc43bdb/artifacts/6q2h4u11_Quando%20o%20design%20prioriza%20o%20bem-estar.Poltrona%20Bruma%2C%20feita%20para%20permanecer..jpg"}
                alt="Poltrona Bruma - Qualidade Artesanal"
                className="w-full h-auto object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 md:py-40 noise-texture bg-primary text-white">
        <div className="px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-heading text-4xl md:text-6xl font-light tracking-tight mb-6">
            Pronto para Transformar<br />Seu Espaço?
          </h2>
          <p className="text-lg font-light leading-relaxed text-white/80 mb-12 max-w-2xl mx-auto">
            Solicite um orçamento personalizado e descubra como nossos móveis podem elevar seu ambiente.
          </p>
          <Link
            to="/contato"
            data-testid="request-quote-button"
            className="inline-flex items-center gap-3 bg-white text-primary px-8 py-4 uppercase tracking-widest text-xs hover:bg-transparent hover:text-white border border-white transition-all duration-500"
          >
            Solicitar Orçamento
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
